import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../shared/auth/store'
import { http } from '../../shared/api/http'
import { logger } from '../../shared/utils/logger'
import { globalWebSocket } from '../../shared/ws/globalWebSocket'
import { queryClient } from '../../modules/chat/providers/QueryProvider'
import FilePreviewModal from '../../shared/components/FilePreviewModal'
import { canPreviewFile, isImageFile, isPdfFile } from '../../shared/utils/fileUtils'
import { 
  FileText, 
  Download, 
  Eye, 
  MessageSquare, 
  Building2, 
  Mail, 
  Calendar,
  Clock,
  User,
  Plus,
  FileImage,
  Archive,
  File,
  Users,
  BarChart3,
  Paperclip,
  CheckCircle,
  X
} from 'lucide-react'

// === TYPES ===

interface TimelineComment {
  id: string
  comment: string
  author: string
  created_at: string
  files: Array<{
    id: string
    name: string
    size?: number
    type?: string
    urlPreview?: string
    urlShow?: string
    urlDownload?: string
  }>
}

interface CompanyInfo {
  id: string
  title: string
  email?: string
}

interface TimelineResponse {
  success: boolean
  timeline: TimelineComment[]
  company_info: CompanyInfo
  current_user_info: {
    first_name: string
    last_name: string
    email: string
  }
  message: string
}

// === HELPER FUNCTIONS ===

// Функция для определения типа файла по расширению
const getFileType = (fileName: string): { icon: React.ComponentType<any>; color: string; type: string } => {
  const ext = fileName.split('.').pop()?.toLowerCase()
  
  switch (ext) {
    case 'pdf':
      return { icon: FileText, color: 'text-red-600 bg-red-50 border-red-200', type: 'PDF' }
    case 'doc':
    case 'docx':
      return { icon: FileText, color: 'text-blue-600 bg-blue-50 border-blue-200', type: 'Word' }
    case 'xls':
    case 'xlsx':
      return { icon: BarChart3, color: 'text-green-600 bg-green-50 border-green-200', type: 'Excel' }
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return { icon: FileImage, color: 'text-purple-600 bg-purple-50 border-purple-200', type: 'Изображение' }
    case 'zip':
    case 'rar':
      return { icon: Archive, color: 'text-yellow-600 bg-yellow-50 border-yellow-200', type: 'Архив' }
    default:
      return { icon: File, color: 'text-gray-600 bg-gray-50 border-gray-200', type: 'Файл' }
  }
}

// Функция для форматирования размера файла
const formatFileSize = (bytes?: number): string => {
  if (!bytes) return ''
  
  const sizes = ['Б', 'КБ', 'МБ', 'ГБ']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`
}

// Функция для извлечения имени автора из комментария
const extractAuthorFromComment = (comment: string): { author: string; cleanComment: string } => {
  // Ищем паттерн [Имя Фамилия]: текст
  const match = comment.match(/^\[([^\]]+)\]:\s*(.*)$/s)
  
  if (match) {
    return {
      author: match[1].trim(), // "Федор Сумароков"
      cleanComment: match[2].trim() // "тест"
    }
  }
  
  // Если паттерн не найден, возвращаем как есть
  return {
    author: '',
    cleanComment: comment
  }
}

// Функция для обработки имени автора
const getAuthorDisplayName = (author: string, comment: string): string => {
  // Сначала пытаемся извлечь имя из комментария
  const { author: extractedAuthor } = extractAuthorFromComment(comment)
  
  if (extractedAuthor) {
    return extractedAuthor // "Федор Сумароков"
  }
  
  // Если не удалось извлечь из комментария, используем системного автора
  if (author.match(/Пользователь\s*\d+/i)) {
    return 'Менеджер'
  }
  
  // Если автор пустой или неопределенный, тоже "Менеджер"
  if (!author || author.trim() === '' || author === 'Неизвестный автор') {
    return 'Менеджер'
  }
  
  // Иначе возвращаем имя как есть
  return author
}

// === TIMELINE ITEM COMPONENT ===

interface TimelineItemProps {
  comment: TimelineComment
  index: number
  onPreviewFile: (fileUrl: string, fileName: string) => void
}

function TimelineItem({ comment, index, onPreviewFile }: TimelineItemProps) {
  const date = new Date(comment.created_at)
  const formattedDate = date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit'
  })
  const formattedTime = date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit'
  })

  // Извлекаем автора и чистый комментарий
  const { author: extractedAuthor, cleanComment } = extractAuthorFromComment(comment.comment)
  const authorName = getAuthorDisplayName(comment.author, comment.comment)
  const displayComment = extractedAuthor ? cleanComment : comment.comment

  // Определяем, является ли автор менеджером или клиентом
  const isManager = authorName === 'Менеджер' || authorName.includes('Менеджер')
  const isClient = !isManager && extractedAuthor && !authorName.includes('Менеджер')
  
  // Если не клиент и не менеджер, то по умолчанию менеджер
  const finalIsManager = !isClient

  // Генерируем цвет для аватара на основе имени
  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-red-500', 
      'bg-yellow-500', 'bg-indigo-500', 'bg-pink-500', 'bg-teal-500'
    ]
    let hash = 0
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash)
    }
    return colors[Math.abs(hash) % colors.length]
  }

  return (
    <div className={`w-full ${
      isClient 
        ? 'bg-gray-100 text-gray-800 border border-gray-300' 
        : 'bg-white text-gray-800 border border-gray-200'
    } rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow mb-4 ${
      isClient ? 'border-l-4 border-l-gray-500' : 'border-l-4 border-l-gray-400'
    }`}>
      {/* Заголовок записи - компактный */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 ${
            isClient ? 'bg-gray-500' : getAvatarColor(authorName)
          } rounded-full flex items-center justify-center text-white font-medium text-xs`}>
            {authorName.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="flex items-center gap-1">
              {isClient ? (
                <User className="w-3 h-3 text-gray-600" />
              ) : (
                <Users className="w-3 h-3 text-gray-500" />
              )}
              <span className="font-medium text-sm text-gray-900">{authorName}</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              <span>{formattedDate} • {formattedTime}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Комментарий - компактный */}
      {displayComment && (
        <div className="mb-3">
          <div className="flex items-start gap-2">
            <MessageSquare className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
              isClient ? 'text-gray-600' : 'text-blue-500'
            }`} />
            <p className="text-sm leading-relaxed text-gray-800">
              {displayComment}
            </p>
          </div>
        </div>
      )}

      {/* Файлы - компактный */}
      {comment.files && comment.files.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-1 text-xs font-medium text-gray-600">
            <Paperclip className="w-3 h-3" />
            <span>Документы ({comment.files.length})</span>
          </div>
          
          <div className="space-y-2">
            {comment.files.map((file, fileIndex) => {
              const fileInfo = getFileType(file.name)
              const IconComponent = fileInfo.icon
              
              return (
                <div
                  key={fileIndex}
                  className={`flex items-center justify-between p-3 rounded-md border transition-colors hover:bg-opacity-50 ${
                    isClient 
                      ? 'bg-gray-50 border-gray-300' 
                      : fileInfo.color
                  }`}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <IconComponent className="w-4 h-4 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate text-sm text-gray-900">
                        {file.name}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="px-1.5 py-0.5 rounded text-xs bg-white">
                          {fileInfo.type}
                        </span>
                        {file.size && (
                          <span>• {formatFileSize(file.size)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 ml-2">
                {file.urlShow && (
                  <button
                    onClick={() => {
                      if (!file.urlShow) return

                      // Если файл можно предпросмотреть, открываем в модальном окне
                      if (canPreviewFile(file.type, file.name)) {
                        // Создаем авторизованный URL для стриминга файла (как в чате)
                        const createAuthorizedUrl = (forImage = false) => {
                          // Для изображений НЕ используем baseURL (который содержит /api/)
                          // Для PDF и других файлов используем baseURL как раньше
                          const baseUrl = forImage ? window.location.origin : (window.location.origin);
                          
                          // Получаем company_id из пользователя
                          const getCompanyId = (): string | null => {
                            const tokens = [
                              localStorage.getItem('alfastil_token'),
                              localStorage.getItem('alfastil_web_token')
                            ]
                            for (const token of tokens) {
                              if (!token) continue
                              try {
                                const payload = JSON.parse(atob(token.split('.')[1]))
                                const now = Math.floor(Date.now() / 1000)
                                if (payload.exp && payload.exp >= now && payload.company_id) {
                                  return payload.company_id.toString()
                                }
                              } catch {
                                continue
                              }
                            }
                            return null
                          }
                          
                          const companyId = getCompanyId()
                          if (!companyId) {
                            console.error('❌ Не удалось определить company_id')
                            return null
                          }
                          
                          const streamPath = `/company-timeline/file/${encodeURIComponent(companyId)}/${encodeURIComponent(file.name)}`
                          
                          // Получаем валидный токен
                          const getValidToken = (): string | null => {
                            const tokens = [
                              localStorage.getItem('alfastil_token'),
                              localStorage.getItem('alfastil_web_token')
                            ]
                            
                            for (const token of tokens) {
                              if (!token) continue
                              try {
                                const payload = JSON.parse(atob(token.split('.')[1]))
                                const now = Math.floor(Date.now() / 1000)
                                if (payload.exp && payload.exp >= now) {
                                  return token
                                }
                              } catch {
                                continue
                              }
                            }
                            return null
                          }
                          
                          const token = getValidToken()
                          
                          if (token) {
                            // Добавляем токен как query параметр для прямых запросов (img, iframe, etc.)
                            const separator = streamPath.includes('?') ? '&' : '?'
                            return `${baseUrl}${streamPath}${separator}auth_token=${encodeURIComponent(token)}`
                          } else {
                            // Fallback без токена
                            return `${baseUrl}${streamPath}`
                          }
                        }
                        
                        // Определяем тип файла и создаем URL
                        const fileUrl = createAuthorizedUrl(true) // Все файлы через /company-timeline/file/
                        
                        
                        if (fileUrl) {
                          // Открываем модальное окно с URL (лоадер будет показан в FilePreviewModal)
                          onPreviewFile(fileUrl, file.name)
                        } else {
                          console.error('❌ Не удалось создать URL для файла')
                        }
                      } else {
                        // Иначе открываем в новой вкладке
                        window.open(file.urlShow, '_blank')
                      }
                    }}
                        className={`p-1.5 rounded transition-colors ${
                          isClient 
                            ? 'bg-gray-500 text-white hover:bg-gray-600' 
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                        title={canPreviewFile(file.type, file.name) ? "Предпросмотр" : "Просмотр"}
                      >
                        <Eye className="w-3 h-3" />
                      </button>
                    )}
                {file.urlDownload && (
                  <button
                    onClick={() => {
                      if (!file.urlDownload) return
                      // Используем прямой URL из Bitrix24 для скачивания
                      window.open(file.urlDownload, '_blank')
                    }}
                        className={`p-1.5 rounded transition-colors ${
                          isClient 
                            ? 'bg-gray-500 text-white hover:bg-gray-600' 
                            : 'bg-green-500 text-white hover:bg-green-600'
                        }`}
                        title="Скачать"
                      >
                        <Download className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// === MAIN COMPONENT ===

export function DocumentsPage() {
  const { user } = useAuth()
  const [timeline, setTimeline] = useState<TimelineComment[]>([])
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null)
  const [currentUserInfo, setCurrentUserInfo] = useState<{first_name: string, last_name: string, email: string} | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [newComment, setNewComment] = useState('')
  const [isAddingComment, setIsAddingComment] = useState(false)
  const [showAddComment, setShowAddComment] = useState(false)
  
  // Состояние для предпросмотра файлов
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean
    fileUrl: string
    fileName: string
  }>({
    isOpen: false,
    fileUrl: '',
    fileName: ''
  })

  // Обработчики предпросмотра файлов
  const openPreviewModal = (fileUrl: string, fileName: string) => {
    setPreviewModal({
      isOpen: true,
      fileUrl,
      fileName
    })
  }

  const closePreviewModal = () => {
    setPreviewModal({
      isOpen: false,
      fileUrl: '',
      fileName: ''
    })
  }

  // Загрузка timeline компании
  const loadTimeline = useCallback(async () => {
    try {
      setIsLoading(true)
      setError('')
      
      logger.info('📋 Загрузка timeline компании...')
      
      const response = await http.get<TimelineResponse>('/company-timeline/')
      
      if (response.data.success) {
        setTimeline(response.data.timeline)
        setCompanyInfo(response.data.company_info)
        setCurrentUserInfo(response.data.current_user_info)
        logger.info(`✅ Загружено ${response.data.timeline.length} записей timeline`)
        logger.info(`👤 Информация о пользователе:`, response.data.current_user_info)
      } else {
        setError(response.data.message || 'Ошибка загрузки timeline')
      }
    } catch (error: any) {
      logger.error('❌ Ошибка загрузки timeline:', error)
      setError(error.response?.data?.detail || 'Ошибка загрузки документов компании')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Добавление комментария
  const handleAddComment = async () => {
    if (!newComment.trim()) return

    try {
      setIsAddingComment(true)
      
      logger.info('💬 Добавление комментария...')
      
      const response = await http.post('/company-timeline/comment', {
        comment: newComment.trim()
      })
      
      if (response.data.success) {
        setNewComment('')
        setShowAddComment(false)
        
        // Добавляем комментарий локально для мгновенного отображения
        const authorName = currentUserInfo?.first_name && currentUserInfo?.last_name 
          ? `${currentUserInfo.first_name} ${currentUserInfo.last_name}`.trim()
          : currentUserInfo?.first_name || currentUserInfo?.last_name || user?.first_name || user?.last_name || 'Вы'
        
        const addedComment: TimelineComment = {
          id: response.data.comment_id || Date.now().toString(),
          comment: newComment.trim(),
          author: authorName,
          created_at: new Date().toISOString(),
          files: []
        }
        
        setTimeline(prev => [addedComment, ...prev])
        logger.info('✅ Комментарий успешно добавлен')
        
        // Инвалидируем кеш для обновления данных в других компонентах
        try {
          queryClient.invalidateQueries({ queryKey: ['company-timeline'] })
          queryClient.invalidateQueries({ queryKey: ['company-info'] })
          logger.info('🔄 Кеш timeline инвалидирован после добавления комментария')
        } catch (cacheError) {
          logger.warn('⚠️ Ошибка инвалидации кеша:', cacheError)
        }
        
        // WebSocket уведомление придет автоматически для других пользователей
      } else {
        setError(response.data.message || 'Ошибка добавления комментария')
      }
    } catch (error: any) {
      logger.error('❌ Ошибка добавления комментария:', error)
      setError(error.response?.data?.detail || 'Ошибка добавления комментария')
    } finally {
      setIsAddingComment(false)
    }
  }

  // Сортируем timeline по дате (новые сначала)
  const sortedTimeline = [...timeline].sort((a, b) => 
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )

  // Группировка записей по датам для лучшего отображения
  const groupedTimeline = sortedTimeline.reduce((groups, comment) => {
    const date = new Date(comment.created_at).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })
    
    if (!groups[date]) {
      groups[date] = []
    }
    groups[date].push(comment)
    return groups
  }, {} as Record<string, TimelineComment[]>)

  useEffect(() => {
    loadTimeline()
  }, [loadTimeline])

  // WebSocket подписка для real-time обновлений
  useEffect(() => {
    const handleTimelineUpdate = (data: any) => {
      logger.info('📡 Получено обновление timeline:', data)
      
      // Проверяем, что это обновление для нашей компании
      if (data.type === 'timeline_comment_added' && companyInfo && data.company_id === companyInfo.id) {
        // Добавляем новый комментарий в начало списка (только если это не наш собственный комментарий)
        const currentUserName = currentUserInfo?.first_name && currentUserInfo?.last_name 
          ? `${currentUserInfo.first_name} ${currentUserInfo.last_name}`.trim()
          : currentUserInfo?.first_name || currentUserInfo?.last_name || user?.first_name || user?.last_name || 'Вы'
        const isOwnComment = data.author === currentUserName || data.author === 'Вы'
        
        if (!isOwnComment) {
          const newComment: TimelineComment = {
            id: data.comment_id,
            comment: data.comment,
            author: data.author || 'Менеджер',
            created_at: data.created_at || new Date().toISOString(),
            files: data.files || []
          }
          
          setTimeline(prev => [newComment, ...prev])
          logger.info('✅ Добавлен новый комментарий от другого пользователя в real-time')
        }
        
        // Инвалидируем кеш для всех связанных запросов
        try {
          queryClient.invalidateQueries({ queryKey: ['company-timeline'] })
          queryClient.invalidateQueries({ queryKey: ['company-info'] })
          logger.info('🔄 Кеш timeline инвалидирован')
        } catch (cacheError) {
          logger.warn('⚠️ Ошибка инвалидации кеша:', cacheError)
        }
      }
    }

    // Подписываемся на события timeline
    globalWebSocket.on('timeline_update', handleTimelineUpdate)
    globalWebSocket.on('company_timeline_comment', handleTimelineUpdate)

    // Отписываемся при размонтировании
    return () => {
      globalWebSocket.off('timeline_update', handleTimelineUpdate)
      globalWebSocket.off('company_timeline_comment', handleTimelineUpdate)
    }
  }, [companyInfo, user])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center px-4">
        <div className="flex items-center gap-3 bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-lg border border-white/20">
          <div className="animate-spin w-5 h-5 border-2 border-blue-200 border-t-blue-500 rounded-full"></div>
          <span className="text-sm font-medium text-gray-700">Загрузка документов...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-4 px-4">
      <div className="max-w-4xl mx-auto">
        
        {/* Кнопка "Назад" */}
        <div className="mb-4">
          <Link 
            to="/orders" 
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 hover:bg-white backdrop-blur-sm border border-gray-200/50 hover:border-gray-300/50 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 text-gray-700 hover:text-gray-900 text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Назад к каталогу</span>
          </Link>
        </div>
        
        {/* Заголовок - компактный */}
        <div className="mb-4">
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            {/* Информация о пользователе - наверху */}
            <div className="mb-3 pb-3 border-b border-gray-100">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-500" />
                  <span className="text-gray-600">Вы вошли как:</span>
                  <span className="font-medium text-gray-900">
                    {currentUserInfo?.first_name && currentUserInfo?.last_name 
                      ? `${currentUserInfo.first_name} ${currentUserInfo.last_name}`.trim()
                      : currentUserInfo?.first_name || currentUserInfo?.last_name || currentUserInfo?.email || user?.email || 'Пользователь'
                    }
                  </span>
                </div>
                {companyInfo && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">—</span>
                    <span className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded">
                      <Building2 className="w-3 h-3" />
                      <span className="font-medium">{companyInfo.title}</span>
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Статистика - наверху */}
            {timeline.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
                <div className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  <span className="font-semibold">{timeline.length}</span>
                  <span className="hidden sm:inline">записей</span>
                  <span className="sm:hidden">зап.</span>
                </div>
                <div className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded">
                  <span className="font-semibold">
                    {timeline.filter(t => t.files && t.files.length > 0).length}
                  </span>
                  <span className="hidden sm:inline">с файлами</span>
                  <span className="sm:hidden">с файлами</span>
                </div>
                <div className="flex items-center gap-1 text-orange-600 bg-orange-50 px-2 py-1 rounded">
                  <Users className="w-3 h-3" />
                  <span className="font-semibold">
                    {new Set(timeline.map(t => getAuthorDisplayName(t.author, t.comment))).size}
                  </span>
                  <span className="hidden sm:inline">авторов</span>
                  <span className="sm:hidden">авт.</span>
                </div>
              </div>
            )}

            <div className="mb-3">
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                Документооборот
              </h1>
            </div>

            {/* Кнопка добавить - отдельно и понятно */}
            <div className="border-t border-gray-100 pt-3 mb-3">
              <button
                onClick={() => setShowAddComment(!showAddComment)}
                className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                <span>Добавить запись</span>
              </button>
            </div>
          </div>
        </div>

        {/* Форма добавления записи - компактная */}
        {showAddComment && (
          <div className="mb-4">
            <div className="bg-green-50 rounded-lg shadow-sm p-4 border border-green-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-green-600" />
                Новая запись
              </h3>
              
              <div className="space-y-3">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Добавьте комментарий или описание..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none text-sm"
                  rows={3}
                  disabled={isAddingComment}
                />
                
                {/* Будущее место для загрузки файлов */}
                <div className="bg-white rounded-md p-3 border-2 border-dashed border-gray-300">
                  <div className="text-center text-gray-500">
                    <Paperclip className="w-4 h-4 mx-auto mb-1" />
                    <p className="text-xs">Загрузка файлов - в разработке</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || isAddingComment}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm font-medium"
                  >
                    {isAddingComment ? (
                      <>
                        <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>Добавление...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span>Добавить</span>
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowAddComment(false)
                      setNewComment('')
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Отмена
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Ошибки */}
        {error && (
          <div className="mb-8">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}


        {/* Timeline документов - компактный */}
        {timeline.length > 0 ? (
          <div className="space-y-4">
            {Object.entries(groupedTimeline)
              .sort(([dateA], [dateB]) => {
                // Сортируем даты в обратном порядке (новые сначала)
                const dateObjA = new Date(dateA.split(' ').reverse().join('-'))
                const dateObjB = new Date(dateB.split(' ').reverse().join('-'))
                return dateObjB.getTime() - dateObjA.getTime()
              })
              .map(([date, comments]) => (
              <div key={date} className="space-y-3">
                {/* Заголовок даты - компактный */}
                <div className="flex items-center gap-3">
                  <div className="bg-blue-500 text-white px-3 py-1 rounded-lg text-xs font-medium flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {date}
                  </div>
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {comments.length} {comments.length === 1 ? 'запись' : comments.length < 5 ? 'записи' : 'записей'}
                  </div>
                </div>

                {/* Записи за день */}
                <div className="space-y-3 ml-2">
                  {comments
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                    .map((comment, index) => (
                    <TimelineItem 
                      key={comment.id} 
                      comment={comment} 
                      index={index}
                      onPreviewFile={openPreviewModal}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg shadow-sm p-8 border border-gray-200 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Документооборот пуст
            </h3>
            <p className="text-gray-600 mb-6 text-sm max-w-sm mx-auto">
              У компании пока нет записей. Добавьте первую запись.
            </p>
            <button
              onClick={() => setShowAddComment(true)}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium text-sm flex items-center gap-2 mx-auto"
            >
              <Plus className="w-4 h-4" />
              <span>Создать первую запись</span>
            </button>
          </div>
        )}

        {/* Модальное окно для предпросмотра файлов */}
        <FilePreviewModal
          isOpen={previewModal.isOpen}
          onClose={closePreviewModal}
          fileUrl={previewModal.fileUrl}
          fileName={previewModal.fileName}
        />

      </div>
    </div>
  )
}
